import { useCMSPageField } from '@/cms/fields';
import {
  Button,
  Typography,
  tk,
  SheetTitle,
  SheetClose,
  SheetHeader,
  SheetOverlay,
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetPortal,
  SheetContentWrapper,
} from '@sunwebgroup/ui-components';
import { useIntl } from 'react-intl';
import { styled } from 'styled-components';
import FacilityType from './FacilityType';
import type { PDPClickEventProps } from '@/scopes/products/infrastructure/analytics';
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import * as React from 'react';

const GeneralFacilitiesContainer = styled.div`
  background-color: ${tk('color_semantic_bg_subdued')};
  margin: ${tk('spacing_lg')} 0;
  padding: ${tk('spacing_lg')};
  padding-bottom: calc(${tk('spacing_lg')} - ${tk('spacing_xs')});
  border-radius: ${tk('radius_large')};
`;

const SheetContentWrapperNoPadding = styled(SheetContentWrapper)`
  background-color: ${tk('color_semantic_bg_subdued')};
  padding: ${tk('spacing_2xl')};
  height: 100%;
  overflow-y: auto;
`;

const GENERAL_FACILITIES_MAX_ITEMS = 8 as const;

export default function Facilities() {
  const intl = useIntl();
  const facilities = useCMSPageField('PDP.facilities');
  const { sendEvent } = useAnalytics();

  if (facilities?.length === 0) {
    return null;
  }

  const openSheetEvent: PDPClickEventProps = {
    element: 'Bottom sheet',
    description: 'Facilities bottom sheet opened',
    result: 'Facilities bottom sheet opened',
  };
  const closeSheetEvent: PDPClickEventProps = {
    element: 'Bottom sheet',
    description: 'Facilities bottom sheet closed',
    result: 'Facilities bottom sheet closed',
  };

  const handleChange = (isOpen: boolean) => {
    if (isOpen) {
      sendEvent(createPDPClickEvent(openSheetEvent));
    } else {
      sendEvent(createPDPClickEvent(closeSheetEvent));
    }
  };

  return (
    <>
      <Typography as="h2" $variant="heading-md">
        {intl.formatMessage({ id: 'practical_info_facilities_title' })}
      </Typography>

      <GeneralFacilitiesContainer>
        <FacilityType facilityType={facilities[0]} maxItems={GENERAL_FACILITIES_MAX_ITEMS} />
      </GeneralFacilitiesContainer>

      <Sheet onOpenChange={handleChange}>
        <SheetTrigger asChild>
          <Button $hierarchy="secondary">{intl.formatMessage({ id: 'practical_info_facilities_cta' })}</Button>
        </SheetTrigger>
        <SheetPortal>
          <SheetOverlay onClick={() => handleChange} />
          <SheetContent $side="bottom">
            <SheetHeader>
              <SheetTitle>{intl.formatMessage({ id: 'practical_info_facilities_title' })}</SheetTitle>
              <SheetClose>{intl.formatMessage({ id: 'close' })}</SheetClose>
            </SheetHeader>
            <SheetContentWrapperNoPadding>
              {facilities.map(
                (facility) => facility.show && <FacilityType key={facility.id} facilityType={facility} />
              )}
            </SheetContentWrapperNoPadding>
          </SheetContent>
        </SheetPortal>
      </Sheet>
    </>
  );
}
